import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { queriesApi } from '../services/queriesService'

/**
 * Hook para obtener datos de notificaciones de Contentful
 * Incluye contenido para configuraciones de notificaciones
 *
 * @returns {Object} Objeto con loading, error, datos procesados y funciones utilitarias
 */
export const useContentfulNotification = () => {
	// Query con Tanstack Query
	const {
		data: rawData,
		isLoading,
		error,
		refetch,
		isStale,
		isRefetching
	} = useQuery({
		queryKey: ['contentful', 'notification'] as const,
		queryFn: queriesApi.getNotification,
		staleTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		cacheTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		retry: 2,
		retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
		refetchOnWindowFocus: false,
		refetchOnReconnect: true
	})

	// Manejar errores
	if (error) {
		console.error('Error al obtener datos de Notification desde Contentful:', error)
	}

	// Procesar contenido de notificaciones
	const referenciaContenidoCollection = useMemo(() => {
		if (!rawData || isLoading || error) return []

		try {
			return (
				rawData?.templateContenidoCollection?.items?.[0]?.referenciaContenidoCollection?.items || []
			)
		} catch (transformError) {
			console.error('Error al procesar contenido de notificaciones:', transformError)
			return []
		}
	}, [rawData, isLoading, error])

	// Información del cache
	const cacheInfo = useMemo(
		() => ({
			isStale,
			isRefetching,
			lastFetch: rawData ? new Date() : null,
			cacheExpiry: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000)
		}),
		[isStale, isRefetching, rawData]
	)

	return {
		// Datos principales (compatibilidad con hook original)
		loading: isLoading,
		referenciaContenidoCollection,

		// Funcionalidades adicionales
		error,
		refetch,
		isStale,
		isRefetching,
		cacheInfo,
		rawData
	}
}

export default useContentfulNotification
